import { useCallback, useEffect, useState } from "react";
import {
  MaterialReactTable,
  useMaterialReactTable,
} from "material-react-table";
import JSZip from "jszip";
import axios from "axios";
import {
  ListSubheader,
  MenuItem,
  Select,
  TextField,
  Stack,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import FileDownloadIcon from "@mui/icons-material/FileDownload";

import {
  useTrainingList,
  useCreateTraining,
  usePatchTraining,
  useDeleteTraining,
  useTrainingRegistration,
  useRetrieveTraining,
} from "../../api/trainingApi";
import {
  RegisteredStudentTableDef,
  StaffTrainingTableDef,
} from "../../table/TrainingTableDef";
import type { TrainingType } from "../../types/training";
import type { Period } from "../../types/period";
import { categoryTranslator, sportTypes } from "../../types/training";
import { departmentOptions, courseOptions } from "../../utils/trainingUtils";
import Dialog from "../../components/Dialog";
import PeriodInput from "../../components/PeriodInput";
import {
  getTranslatedPeriod,
  getSortedPeriod,
  getTotalTime,
  getStringifiedPeriod,
} from "../../utils/periodUtils";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { koKR } from "@mui/x-date-pickers/locales";
import Table from "../../components/Table";
import { getTimezoneOffsetAddedTime } from "../../utils/fileUtils";
import { useGetInstructors } from "../../api/instructorApi";
import { Instructor } from "../../types/instructor";

const getSportTypeOptions = () => {
  const converted: { value: string; label: string; type: "header" | "item" }[] =
    [];
  Object.entries(sportTypes).forEach(([key, value]) => {
    converted.push({
      value: key,
      label: categoryTranslator[key],
      type: "header",
    });
    value.forEach((sport) => {
      converted.push({ value: sport, label: sport, type: "item" });
    });
  });

  return converted;
};

const getSportSubEventOptions = (sport_event: string) => {
  return Object.values(sportTypes).find((sports) =>
    sports.includes(sport_event)
  );
};

const EditTraining = ({
  original,
  close,
  submit,
}: {
  original: any;
  close: () => void;
  submit: (data: any) => Promise<void>;
}) => {
  const [editData, setEditData] = useState<
    Record<string, any> & { schedule?: Period[] }
  >({});
  const { data: instructors } = useGetInstructors();

  useEffect(() => {
    setEditData({
      ...original,
      schedule: getTranslatedPeriod(original.schedule) || [{}],
    });
  }, [original]);

  const saveEditData = useCallback((key: string, value: any) => {
    setEditData((prev) => ({ ...prev, [key]: value }));
  }, []);

  const saveSchedule = useCallback((idx: number, schedule: any) => {
    setEditData((prev) => ({
      ...prev,
      schedule: [
        ...(prev?.schedule?.slice(0, idx) || []),
        schedule,
        ...(prev?.schedule?.slice(idx + 1) || []),
      ],
    }));
  }, []);

  const handleSubmit = async () => {
    if (editData.schedule) {
      const sortedPeriod = getSortedPeriod(editData.schedule);

      for (let i = 0; i < sortedPeriod.length; i++) {
        const currentPeriod = sortedPeriod[i];
        if (
          !currentPeriod.date ||
          !currentPeriod.start_time ||
          !currentPeriod.end_time
        ) {
          continue;
        }
      }

      for (let i = 0; i < editData.schedule.length; i++) {
        const p = editData.schedule[i] as any;
        if (!p.date && !p.start_time && !p.end_time) {
          continue;
        }

        if (!p.date || !p.start_time || !p.end_time) {
          alert("날짜, 시작 시간, 종료 시간을 모두 입력해주세요.");
          return;
        }

        if (p.end_time <= p.start_time) {
          alert("종료 시간이 시작 시간보다 빠릅니다.");
          return;
        }
      }
    }

    if (editData.student_limit) {
      const toNum = parseInt(editData.student_limit, 10);
      if (isNaN(toNum) || toNum < 0) {
        alert("정원은 0 이상의 숫자여야 합니다.");
        return;
      }
    }

    if (
      !editData.department ||
      !editData.course?.length ||
      !editData.sport_event ||
      !editData.instructor ||
      !editData.schedule?.length ||
      !editData.student_limit
    ) {
      alert("모든 항목을 입력해주세요.");
      return;
    }

    const data = Object.fromEntries(
      Object.entries(editData).filter(([key, value]) => value !== original[key])
    );
    if (data.schedule) {
      data.schedule = getStringifiedPeriod(getSortedPeriod(data.schedule));
    }
    data.id = original.id;

    try {
      await submit(data);
      alert("성공적으로 등록했습니다.");
      close();
    } catch (e) {
      console.error(e);
      alert("추가/수정 중 오류가 발생했습니다.");
    }
  };

  return (
    <Dialog
      title="실습 추가/수정"
      buttons={["close", "ok"]}
      ok={async () => {
        await handleSubmit();
      }}
      close={close}
    >
      <div className="binary-grid" style={{ width: "800px" }}>
        <>
          <p>구분</p>
          <Select
            value={editData.department || ""}
            variant="standard"
            onChange={(e) => {
              saveEditData("department", e.target.value);
            }}
          >
            {departmentOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </>
        <>
          <p>과정</p>
          <Select
            value={editData.course || []}
            variant="standard"
            multiple
            onChange={(e) => {
              const { value } = e.target;
              saveEditData(
                "course",
                typeof value === "string" ? value.split(",") : value
              );
            }}
          >
            {courseOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </>
        <>
          <p>종목</p>
          <Select
            value={editData.sport_event || ""}
            variant="standard"
            onChange={(e) => {
              saveEditData("sport_event", e.target.value);
              saveEditData("sport_sub_events", []);
            }}
          >
            {getSportTypeOptions().map(({ value, type, label }) =>
              type === "header" ? (
                <ListSubheader
                  sx={{
                    color: "var(--primary-500)",
                    padding: "4px 8px",
                    lineHeight: 2,
                  }}
                  key={value}
                >
                  {label}
                </ListSubheader>
              ) : (
                <MenuItem
                  sx={{ fontSize: "0.9em", padding: "2px 16px" }}
                  key={value}
                  value={value}
                >
                  {label}
                </MenuItem>
              )
            )}
          </Select>
        </>
        <>
          <p>유사종목</p>
          <Select
            variant="standard"
            multiple
            value={editData.sport_sub_events || []}
            onChange={(e) => {
              const { value } = e.target;
              saveEditData(
                "sport_sub_events",
                typeof value === "string" ? value.split(",") : value
              );
            }}
          >
            {getSportSubEventOptions(editData.sport_event || "")?.map(
              (sport) => (
                <MenuItem key={sport} value={sport}>
                  {sport}
                </MenuItem>
              )
            )}
          </Select>
        </>
        <>
          <p>지도자</p>
          <Select
            variant="standard"
            value={editData.instructor || null}
            onChange={(e) => {
              saveEditData("instructor", e.target.value);
            }}
          >
            {instructors?.map((instructor: Instructor) => (
              <MenuItem key={instructor.id} value={instructor.id}>
                {instructor.id}. {instructor.corp_name} / {instructor.name} /{" "}
                {instructor.corp_address}
              </MenuItem>
            ))}
          </Select>
        </>
        <>
          <p>일정</p>
          <div>
            <div className="period-wrapper">
              <LocalizationProvider
                dateAdapter={AdapterMoment}
                localeText={
                  koKR.components.MuiLocalizationProvider.defaultProps
                    .localeText
                }
              >
                {editData.schedule?.map((period, idx) => (
                  <PeriodInput
                    inputKey={idx.toString()}
                    period={period}
                    savePeriod={(p) => saveSchedule(idx, p)}
                    deletePeriod={() => {
                      setEditData((prev) => ({
                        ...prev,
                        schedule: [
                          ...(prev.schedule?.slice(0, idx) || []),
                          ...(prev.schedule?.slice(idx + 1) || []),
                        ],
                      }));
                    }}
                  />
                ))}
                <button
                  onClick={() =>
                    setEditData((prev) => ({
                      ...prev,
                      schedule: [
                        ...(prev.schedule || []),
                        { date: "", start_time: "", end_time: "" },
                      ],
                    }))
                  }
                >
                  추가
                </button>
              </LocalizationProvider>
            </div>
          </div>
        </>
        <>
          <p>총 시간</p>
          <p>{getTotalTime(editData.schedule || [])}분</p>
        </>
        <>
          <p>정원</p>
          <TextField
            variant="standard"
            fullWidth
            value={editData.student_limit || ""}
            onChange={(e) => {
              saveEditData("student_limit", parseInt(e.target.value, 10));
            }}
            type="number"
          />
        </>
      </div>
    </Dialog>
  );
};

const DetailTraining = ({ id, close }: { id: number; close: () => void }) => {
  const { data: trainingRegistrations } = useTrainingRegistration({ id });
  const { data: trainingDetail } = useRetrieveTraining({ id }) as {
    data?: TrainingType;
  };

  const downloadGallery = async () => {
    if (!trainingDetail?.gallery_urls || !trainingDetail?.gallery_urls.length) {
      alert("사진이 없습니다.");
      return;
    }

    const zip = new JSZip();
    for (const { url, filename } of trainingDetail?.gallery_urls || []) {
      const response = await axios.get(url, { responseType: "blob" });
      const blob = new Blob([response.data]);
      zip.file(filename, blob, {
        date: getTimezoneOffsetAddedTime(),
      });
    }

    try {
      const zipFile = await zip.generateAsync({ type: "blob" });
      const url = URL.createObjectURL(zipFile);
      const a = document.createElement("a");
      a.href = url;
      a.download = `실습사진_${trainingDetail?.instructor_detail.corp_name}_${trainingDetail?.id}.zip`;
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <Dialog title="실습 상세" buttons={["ok"]} ok={close}>
      <div style={{ width: "400px" }}>
        <h4 style={{ marginTop: 0 }}>신청자 목록</h4>
        <Table
          columns={RegisteredStudentTableDef}
          data={trainingRegistrations?.students || []}
          columnPinning={{}}
        />
        <h4 className="horizontal">
          출석부
          {trainingDetail?.attendance_book_url && (
            <a
              href={trainingDetail.attendance_book_url}
              style={{ marginBottom: "-8px" }}
            >
              <FileDownloadIcon />
            </a>
          )}
        </h4>
        <h4 className="horizontal">
          사진
          {trainingDetail?.gallery_urls.length !== 0 && (
            <FileDownloadIcon
              style={{ marginBottom: "-8px" }}
              onClick={downloadGallery}
            />
          )}
        </h4>
        {trainingDetail?.gallery_urls.length !== 0 &&
          trainingDetail?.gallery_urls.map(({ filename, url }) => {
            return (
              <div className="horizontal">
                <p>{filename}</p>
                <a
                  className="no-border-button"
                  href={url}
                  target="_blank"
                  rel="noreferrer"
                >
                  <VisibilityIcon />
                </a>
              </div>
            );
          })}
      </div>
    </Dialog>
  );
};

const Training = () => {
  const { data } = useTrainingList();
  const [editTraining, setEditTraining] = useState<number | null>(null);
  const [detailTraining, setDetailTraining] = useState<number | null>(null);
  const [createTraining, setCreateTraining] = useState<boolean>(false);

  const { mutateAsync: createTrainingApi } = useCreateTraining();
  const { mutateAsync: patchTrainingApi } = usePatchTraining();
  const { mutateAsync: deleteTrainingApi } = useDeleteTraining();

  const trainingList: TrainingType[] = data || [];

  const trainingTable = useMaterialReactTable<TrainingType>({
    data: trainingList || [],
    columns: StaffTrainingTableDef(setEditTraining, deleteTrainingApi),
    defaultColumn: { minSize: 0, maxSize: 200, size: 0 },
    enableFilters: false,
    enablePagination: false,
    enableSorting: false,
    enableHiding: false,
    enableDensityToggle: false,
    enableColumnActions: false,
    enableFullScreenToggle: false,
    enableTopToolbar: false,
    enableBottomToolbar: false,
    state: {
      density: "compact",
    },
    muiTableBodyCellProps: { align: "center" },
    muiTableHeadCellProps: { align: "center" },
    muiTableBodyRowProps: ({ row }) => ({
      onClick: (e) => {
        setDetailTraining(row.original.id);
      },
    }),
    muiTableFooterProps: {},
  });

  return (
    <Stack spacing={2}>
      <Stack direction="row" spacing={3}>
        <h3>현장실습 목록</h3>
        <button onClick={() => setCreateTraining(true)}>추가하기</button>
      </Stack>
      <MaterialReactTable table={trainingTable} />
      {editTraining && (
        <EditTraining
          original={
            trainingList?.find((training) => training.id === editTraining) || {}
          }
          close={() => setEditTraining(null)}
          submit={async (data) => {
            await patchTrainingApi(data);
          }}
        />
      )}
      {createTraining && (
        <EditTraining
          original={{}}
          close={() => setCreateTraining(false)}
          submit={async (data) => {
            await createTrainingApi(data);
          }}
        />
      )}
      {detailTraining && (
        <DetailTraining
          id={detailTraining}
          close={() => setDetailTraining(null)}
        />
      )}
    </Stack>
  );
};

export default Training;
